import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/app/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { apiV2, ApiV2Error } from '@/lib/api-v2-client'
import { useUpgradePrompt } from '@/components/upgrade-prompt'
import { useAuth } from '@/hooks/use-auth'
import { FREE_PLAN } from '@/lib/free-plan'
import { isFreeTier } from '@/lib/subscription-access'
import { toast } from 'sonner'
import { CatPeeking } from '@/components/mascot'

export const Route = createFileRoute('/_authenticated/dashboard/status-pages/')({
  component: StatusPagesPage,
})

function StatusPagesPage() {
  const queryClient = useQueryClient()
  const { organization } = useAuth()
  const freeTier = isFreeTier(organization)
  const { show: showUpgradePrompt } = useUpgradePrompt()
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['status-pages'],
    queryFn: () => apiV2.listStatusPages(),
  })

  const statusPages = data?.data

  const createMutation = useMutation({
    mutationFn: () => apiV2.createStatusPage({ name, slug, description: description || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['status-pages'] })
      toast.success('Status page created')
      setShowCreate(false)
      setName('')
      setSlug('')
      setDescription('')
    },
    onError: (err) => {
      if (err instanceof ApiV2Error && err.status === 402) {
        showUpgradePrompt(err.message)
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to create status page')
      }
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiV2.deleteStatusPage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['status-pages'] })
      toast.success('Status page deleted')
    },
  })

  // Auto-generate slug from name
  function handleNameChange(value: string) {
    setName(value)
    const generated = value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
    setSlug(generated)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Status Pages"
        description={
          freeTier
            ? `Create a public status page for your services. The free plan includes ${FREE_PLAN.statusPages}; Pro includes 5.`
            : 'Create public status pages for your services'
        }
        primaryAction={<Button onClick={() => setShowCreate(true)}>Create status page</Button>}
      />

      {/* Create Form */}
      {showCreate && (
        <Card>
          <CardHeader>
            <CardTitle>New Status Page</CardTitle>
            <CardDescription>Create a public page to display your service status</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                createMutation.mutate()
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="sp-name">Name</Label>
                <Input
                  id="sp-name"
                  placeholder="Acme Status"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sp-slug">Slug</Label>
                <Input
                  id="sp-slug"
                  placeholder="acme-status"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  required
                  pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
                  minLength={3}
                />
                <p className="text-xs text-muted-foreground">
                  Your page will be available at /status/{slug || 'your-slug'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sp-desc">Description (optional)</Label>
                <Input
                  id="sp-desc"
                  placeholder="Current status of Acme services"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreate(false)
                    setName('')
                    setSlug('')
                    setDescription('')
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle>Your Status Pages</CardTitle>
          <CardDescription>
            Public pages showing your service status to users
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="h-20 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : statusPages && statusPages.length > 0 ? (
            <div className="space-y-4">
              {statusPages.map((page) => (
                <div
                  key={page.id}
                  className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="break-words font-medium">{page.name}</span>
                      {page.is_public ? (
                        <StatusBadge kind="up" label="Public" />
                      ) : (
                        <Badge variant="secondary">Private</Badge>
                      )}
                    </div>
                    <p className="break-words text-sm text-body">
                      {page.description || 'No description'}
                    </p>
                    <p className="break-all font-mono text-xs text-muted-foreground">
                      /status/{page.slug}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href={`/status/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/dashboard/status-pages/$id/edit" params={{ id: page.id }}>
                        Edit
                      </Link>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm('Delete this status page?')) {
                          deleteMutation.mutate(page.id)
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <CatPeeking size={120} className="mx-auto mb-4" />
              <p className="mb-4 text-muted-foreground">
                No status pages yet. Create one to show your users the status of your services.
              </p>
              <Button onClick={() => setShowCreate(true)}>
                Create Your First Status Page
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
